
import { useState, useCallback, useEffect } from "react";
import { StartScreen } from "@/components/game/screens/StartScreen";
import { usePuzzleData } from "@/hooks/usePuzzleData";
import { useQueryClient } from "@tanstack/react-query";
import { useScheduledGameClose } from "@/hooks/useScheduledGameClose"; 
import { getTodayString } from "@/utils/dateUtils";
import { LoadingSpinner } from "@/components/game/LoadingSpinner";
import { useToast } from "@/components/ui/use-toast";
import { isGameCompleted, getLastGameResult, clearGameCompletionState } from "@/lib/storage/local-storage";

export default function Index() {
  
  const [email, setEmail] = useState<string | null>(localStorage.getItem("user_email"));
  const [hasCompletedToday, setHasCompletedToday] = useState<boolean>(false);
  const [initialLoading, setInitialLoading] = useState<boolean>(true);
  const { toast } = useToast();
  
  // Get today's date to use as a cache key
  const today = getTodayString();
  
  // Get spreadsheet URL from localStorage or use the default
  const spreadsheetUrl = localStorage.getItem("external_puzzle_url") || 
    "https://docs.google.com/spreadsheets/d/1eLxTlh8OqQhHK_YY6lDyI5FQ5NcntiHC5miJsu2WQjQ/edit?usp=sharing";
  
  const { existingAttempt, todaysPuzzle, isLoading, generateNewPuzzle, error, isUsingExternalData } = 
    usePuzzleData(email, spreadsheetUrl);
    
  const queryClient = useQueryClient();

  // Add the scheduled game close hook
  useScheduledGameClose();

  // Check if the user has already completed today's puzzle
  useEffect(() => {
    // Check if today's puzzle is completed
    const completedGameCheck = today ? isGameCompleted(today) : false;
    const lastResult = getLastGameResult();
    
    // CRITICAL FIX: Check if the lastResult is for TODAY, not any other day
    const isForToday = lastResult && lastResult.completedDate === today;
    
    // Only consider the puzzle completed if it's explicitly for TODAY
    const isCompleted = completedGameCheck || (isForToday);
    
    console.log("Index: Checking for puzzle completion:", {
      completedGameCheck,
      isCompleted,
      todayString: today,
      hasLastResult: !!lastResult,
      lastResultDate: lastResult?.completedDate,
      currentServerTime: new Date().toISOString()
    });

    if (isCompleted) {
      console.log("User has already completed today's puzzle");
      setHasCompletedToday(true);
    } else {
      setHasCompletedToday(false);
    }
  }, [today]);

  // Force refresh puzzle data at midnight or when first loading the page each day
  useEffect(() => {
    const lastLoadDate = localStorage.getItem("last_puzzle_load_date");
    
    // CRITICAL FIX: If the date has changed, we need to reset completion status
    if (lastLoadDate !== today) {
      console.log("New day detected or first load of the day, refreshing puzzle data");
      console.log("Date transition:", { lastLoadDate, today });
      
      localStorage.setItem("last_puzzle_load_date", today);
      
      // CRITICAL FIX: Clear completion status if it's a new day
      if (lastLoadDate) {
        console.log("Date has changed from", lastLoadDate, "to", today, "- clearing completion status");
        clearGameCompletionState();
        setHasCompletedToday(false);
        
        // Force refresh the puzzle data
        setTimeout(() => {
          console.log("Forcing puzzle refresh after date change");
          generateNewPuzzle();
        }, 500);
      } else {
        // Small delay to let the UI render first
        setTimeout(() => {
          generateNewPuzzle();
        }, 500);
      }
    }
    
    // Always ensure spreadsheet URL is set
    if (!localStorage.getItem("external_puzzle_url")) {
      localStorage.setItem("external_puzzle_url", spreadsheetUrl);
    }
  }, [today, generateNewPuzzle, spreadsheetUrl]);

  // Add function to reset completion status (for debugging/development)
  const handleClearCompletedStatus = useCallback(() => {
    clearGameCompletionState();
    setHasCompletedToday(false);
    toast({
      title: "Completion status cleared",
      description: "You can now play today's game again",
      duration: 3000,
    });
    
    // Small delay then refresh
    setTimeout(() => window.location.reload(), 1000);
  }, [toast]);

  const handleGameStart = useCallback((userEmail: string) => {
    setEmail(userEmail);
    localStorage.setItem("user_email", userEmail);
  }, []);

  // Handle retry with clear notification
  const handleRetry = useCallback(() => {
    // Reset any error state
    setInitialLoading(true);
    
    // Small delay so loading dialog can be seen
    setTimeout(() => {
      generateNewPuzzle();
    }, 500);
  }, [generateNewPuzzle]);

  // Control initial loading state
  useEffect(() => {
    // Track when we have data or an error
    if (initialLoading && (todaysPuzzle || error)) {
      // Add a small delay to ensure smooth transition and all data is ready
      const timer = setTimeout(() => {
        console.log("Setting initial loading to false, puzzle data ready:", !!todaysPuzzle);
        setInitialLoading(false);
      }, 800); // Slightly longer delay to ensure everything is loaded
      
      return () => clearTimeout(timer);
    }
  }, [todaysPuzzle, error, initialLoading]);

  // CRITICAL CHANGE: Always show loading spinner during loading states
  // Show full-screen loader during initial load with simplified UI
  if (initialLoading || isLoading) {
    return (
      <div className="fixed inset-0 bg-gray-100 flex flex-col items-center justify-center z-50">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <StartScreen
      existingAttempt={existingAttempt}
      todaysPuzzle={todaysPuzzle}
      onGameStart={handleGameStart}
      isLoading={false} // Critical fix: Don't pass isLoading here since we're already handling it above
      error={error}
      generateNewPuzzle={handleRetry}
      hasCompletedToday={hasCompletedToday}
      isUsingExternalData={isUsingExternalData}
      onClearCompletedStatus={handleClearCompletedStatus}
    />
  );
}
